import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import {
  OnDestinationChangeParams,
  RecentSearchParams,
  SearchLocationType,
  useSearchBox,
  useSearchBoxDestinationPicker
} from 'hooks/searchBox'
import {hideOverlay} from 'modules/overlay/slice'
import {
  getSuggestions,
  getSuggestionSearchValue
} from 'modules/searchBox/selectors'
import {closeSearchBox, setSuggestions} from 'modules/searchBox/slice'
import {buildUserSuggestions} from 'services/search/Suggests'
import {getSearchUrlParams, isValidFreeTextSearch} from 'utils/freeTextSearch'
import {removeUserSearchHistoryById} from 'utils/persistence'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {SuggestionItemDataType} from '@vio/core/src/autocomplete/types'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {NoResults} from './NoResults'
import {
  SuggestionsListElement,
  SuggestionsListSection,
  UserHistoryHeadingElement
} from './styles'
import {SuggestionItem} from './SuggestionItem'
import {SuggestionNearby} from './SuggestionNearby'
import {getShowNearbySuggestion} from './utils'

export enum SuggestionsListVariant {
  Overlay = 'overlay',
  Popover = 'popover'
}

export interface Props {
  onClose?: () => void
  onChange: (params: OnDestinationChangeParams) => void
  onRecentSearchSelected: (params: RecentSearchParams) => void
  onSuggestionSelected: () => void
  onNearbyPropertiesSuggestionSelected: () => void
  listType?: SuggestionsListVariant
}

export const SuggestionsList: React.FC<Props> = ({
  onClose,
  onChange,
  onRecentSearchSelected,
  onSuggestionSelected,
  onNearbyPropertiesSuggestionSelected,
  listType = SuggestionsListVariant.Overlay
}) => {
  const {doSearch} = useSearchBox()
  const dispatch = useDispatch()
  const {isMobile} = useDeviceLayout()

  const suggestionsData = useSelector(getSuggestions)
  const suggestionsSearchValue = useSelector(getSuggestionSearchValue)
  const {destinationPickerInputDisplayValue} = useSearchBoxDestinationPicker()
  const showNearbySuggestion = getShowNearbySuggestion({
    suggestionsSearchValue,
    destinationPickerInputDisplayValue
  })
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1)

  const flattenedSuggestions = useMemo(() => {
    return suggestionsData.reduce(
      (acc, {suggestions}) => [...acc, ...suggestions],
      []
    )
  }, [suggestionsData])

  useEffect(() => {
    setSelectedSuggestion(-1)
  }, [flattenedSuggestions])

  const handleSuggestionClick = useCallback(
    (suggestion: SuggestionItemDataType, index: number) => () => {
      const {
        boundingBox,
        isUserSearchHistory,
        objectDisplayName,
        objectID,
        objectType,
        objectName,
        checkIn,
        checkOut,
        rooms,
        isFreeTextSearch
      } = suggestion

      if (isUserSearchHistory) {
        if (checkIn && checkOut && rooms) {
          const searchHistoryContext = {
            checkIn,
            checkOut,
            hotelId: objectType === 'hotel' ? objectID : undefined,
            placeId: objectType === 'place' ? objectID : undefined,
            address: objectType === 'address' ? objectName : undefined,
            ...(boundingBox && {boundingBox}),
            rooms
          }

          dispatch(
            trackEvent({
              category: Category.User,
              entity: Entity.DestinationPickerSearchHistoryItem,
              action: Action.Clicked,
              analyticsContext: {
                [AnalyticsContext.SearchHistoryContext]: searchHistoryContext,
                [AnalyticsContext.AutocompleteContext]: {
                  suggestion,
                  position: index + 1
                }
              }
            })
          )

          const suggestionType = objectType as SearchLocationType
          const suggestionValue =
            suggestionType === 'area'
              ? boundingBox
              : // In case of hotel or place search
                objectID || objectName

          onRecentSearchSelected({
            type: suggestionType,
            value: suggestionValue,
            checkIn,
            checkOut,
            rooms,
            objectName
          })
        }
      } else if (isFreeTextSearch) {
        const newUrlParams = getSearchUrlParams(suggestion)
        const isValidSearch = isValidFreeTextSearch(newUrlParams)
        if (isValidSearch) {
          dispatch(
            trackEvent({
              category: Category.User,
              entity: Entity.DestinationPickerFreeTextSearchItem,
              action: Action.Clicked,
              analyticsContext: {
                [AnalyticsContext.AutocompleteContext]: {
                  suggestion,
                  position: index + 1,
                  inputtedString: suggestionsSearchValue
                }
              }
            })
          )
          // isFreeTextSearch already drops the filters, so this can push and
          // leave the previous search reachable with back.
          doSearch({
            newUrlParams,
            searchOptions: {method: 'push'},
            isFreeTextSearch: true
          })
          setTimeout(() => {
            dispatch(hideOverlay())
            dispatch(closeSearchBox())
            onClose?.()
          }, 500)
        }
      } else {
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.DestinationPickerAutocompleteItem,
            action: Action.Clicked,
            analyticsContext: {
              [AnalyticsContext.AutocompleteContext]: {
                suggestion,
                position: index + 1,
                inputtedString: suggestionsSearchValue
              }
            }
          })
        )

        onChange({
          displayValue: objectDisplayName,
          value: objectType === 'address' ? objectName : objectID,
          type: objectType as SearchLocationType
        })

        onSuggestionSelected()
      }
    },
    [
      onRecentSearchSelected,
      onChange,
      onSuggestionSelected,
      dispatch,
      doSearch,
      onClose,
      suggestionsSearchValue
    ]
  )

  useEffect(() => {
    if (listType !== 'popover') {
      return
    }

    const keyboardHandler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        setSelectedSuggestion(Math.max(selectedSuggestion - 1, -1))
      }

      if (e.key === 'ArrowDown') {
        setSelectedSuggestion(
          Math.min(selectedSuggestion + 1, flattenedSuggestions.length - 1)
        )
      }

      if (e.key === 'Enter') {
        const index = Math.max(selectedSuggestion, 0)
        const suggestion = flattenedSuggestions[index]
        if (suggestion) {
          handleSuggestionClick(suggestion, index)()
        }
      }
    }

    document.addEventListener('keydown', keyboardHandler)

    return () => {
      document.removeEventListener('keydown', keyboardHandler)
    }
  }, [
    listType,
    selectedSuggestion,
    flattenedSuggestions,
    handleSuggestionClick
  ])

  useEffect(() => {
    const autoCompleteItems = suggestionsData
      .filter(({type}) => type === 'apiSuggestions')
      .flatMap(({suggestions}) => suggestions)

    if (suggestionsSearchValue.length === 0 && autoCompleteItems.length === 0) {
      return
    }

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.DestinationPickerAutoCompleteItems,
        action: Action.Displayed,
        payload: {
          autoCompleteItems,
          inputtedString: suggestionsSearchValue
        }
      })
    )
  }, [dispatch, suggestionsData, suggestionsSearchValue])

  const handleSuggestionListUpdate = () => {
    const userSuggestionData = buildUserSuggestions()

    // This part of the code removes the old userSuggestion and replace it with a new one
    const cleanedData = suggestionsData.filter(
      ({type}) => type !== 'userSearchHistorySuggestions'
    )

    // We need to dispatch the new list in order to don't loos any results on the list
    dispatch(
      setSuggestions({
        suggestions: [...userSuggestionData, ...cleanedData],
        suggestionsSearchValue: ''
      })
    )
  }

  const handleRemoveHistoryClick = historyId => {
    removeUserSearchHistoryById(historyId)
    handleSuggestionListUpdate()
  }

  if (!suggestionsData.length && !showNearbySuggestion)
    return <NoResults listType={listType} />

  return (
    <SuggestionsListElement
      className={cx(
        listType === 'overlay' && '--is-overlay',
        listType === 'popover' && '--is-popover'
      )}
    >
      {showNearbySuggestion && (
        <SuggestionNearby onClick={onNearbyPropertiesSuggestionSelected} />
      )}
      {suggestionsData?.map(({suggestions, type}, sectionIndex) => {
        const sectionOffset = suggestionsData
          .slice(0, sectionIndex)
          .reduce((acc, {suggestions}) => acc + suggestions.length, 0)

        return (
          <SuggestionsListSection
            key={`${type}-${sectionIndex}`}
            listType={listType}
          >
            {isMobile && type === 'userSearchHistorySuggestions' && (
              <UserHistoryHeadingElement>
                <FormattedMessageWrapper
                  id="recentSearches"
                  defaultMessage="Recent searches"
                />
              </UserHistoryHeadingElement>
            )}
            {suggestions.map((suggestion, i) => {
              const index = sectionOffset + i
              const key = [suggestion.objectID, suggestion.objectName, i].join(
                '_'
              )
              const onClick = handleSuggestionClick(suggestion, index)
              return (
                <div key={key} onClick={onClick} role="presentation">
                  <SuggestionItem
                    suggestionDetails={suggestion}
                    isFocused={selectedSuggestion === index}
                    onUserHistoryCloseIconClick={() =>
                      handleRemoveHistoryClick(suggestion.historyId ?? '')
                    }
                  />
                </div>
              )
            })}
          </SuggestionsListSection>
        )
      })}
    </SuggestionsListElement>
  )
}
